import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import Tabs from '../components/common/Tab/Tabs';
import CommonSelect, { Option } from '../components/common/commonSelect/CommonSelect';
import { useDivisionOptions, useTeamOptions } from '../state/master';
import { useUserProfile, isAdminAuthority } from '../context/UserProfileContext';
import { getCognitoUserAttributes } from '../context/UserContext';
import { apiClient } from '../lib/apiClient';
import { API_ENDPOINTS, UI_VISIBILITY, CLASSIFICATION_MARKET_TARGET_ATTR_NAME, MARKET_CATEGORY_OPTIONS } from '../constants';
import './ClassificationMarket.css';

// 選択肢を持つ属性種別のみを「週報分類」候補として扱う
const OPTION_ATTR_TYPES = new Set([11, 12, 13]);

type AttributeOptionRow = {
  option_id: number;
  value: string;
  label: string;
  color?: string | null;
  order_number: number;
  status: string;
};

type AttributeRow = {
  attr_id: number;
  attr_name: string;
  attr_type: number;
  department_id: number;
  team_id: number;
  status: string;
  options: AttributeOptionRow[];
};

type MarketItem = {
  market_option_id: number;
  value: string;
  label: string;
  color?: string | null;
  order_number: number;
  source_department_id: number;
  source_team_id: number;
  source_attr_id?: number | null;
  source_option_id?: number | null;
  category_id: number;
  visibility_scope: 'public' | 'limited';
  target_department_ids: number[];
  status: string;
};

type MarketUsingItem = {
  market_option_id: number;
  department_id: number;
  team_id: number;
  attr_id: number;
  imported_option_id: number;
  created_option?: string;
  create_dt?: string | null;
  status: string;
};

const ClassificationMarketPage: React.FC = () => {
  const { profile } = useUserProfile();
  const canEdit = isAdminAuthority(profile?.authority);

  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const [division, setDivision] = useState<number>(0);
  const [team, setTeam] = useState<number>(0);
  const [attrId, setAttrId] = useState<number | null>(null);

  const [attributes, setAttributes] = useState<AttributeRow[]>([]);
  const [marketItems, setMarketItems] = useState<MarketItem[]>([]);
  const [usingItems, setUsingItems] = useState<MarketUsingItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [publishTarget, setPublishTarget] = useState<AttributeOptionRow | null>(null);
  const [publishScope, setPublishScope] = useState<'public' | 'limited'>('public');
  const [publishCategory, setPublishCategory] = useState<number>(MARKET_CATEGORY_OPTIONS[0].value);
  const [publishTargetDepts, setPublishTargetDepts] = useState<number[]>([]);
  const [email, setEmail] = useState<string>('');
  const [marketSearch, setMarketSearch] = useState<string>('');
  const [marketCategoryFilter, setMarketCategoryFilter] = useState<string>('');

  // 課の初期選択（プロフィールの department から解決。MasterManage と同様のロジック）
  const initDivisionDoneRef = React.useRef(false);
  useEffect(() => {
    if (initDivisionDoneRef.current) return;
    const list: any[] = Array.isArray(divisionOptions) ? divisionOptions : [];
    if (!list.length) return;

    const norm = (s: any) => String(s ?? '').trim().toUpperCase();
    const target = norm(profile?.department ?? '');
    const hit = target
      ? list.find((opt: any) => norm(opt?.value) === target || norm(opt?.label) === target)
      : null;
    const deptId = Number(hit?.department_id ?? list[0]?.department_id ?? NaN);
    if (Number.isFinite(deptId) && deptId > 0) {
      setDivision(deptId);
      initDivisionDoneRef.current = true;
    }
  }, [profile?.department, divisionOptions]);

  // 課に応じたチームの自動選択
  useEffect(() => {
    const list = (Array.isArray(teamOptions) ? teamOptions : []).filter(
      (t: any) => Number(t?.department_id) === Number(division)
    );
    if (list.length === 0) {
      setTeam((prev) => (prev !== 0 ? 0 : prev));
      return;
    }
    setTeam((prev) => {
      const exists = list.some((t: any) => Number(t?.team_id) === Number(prev));
      return exists ? prev : Number(list[0]?.team_id ?? 0);
    });
  }, [division, teamOptions]);

  useEffect(() => {
    (async () => {
      try {
        const attrs = await getCognitoUserAttributes();
        setEmail((attrs?.email as string) ?? '');
      } catch {
        setEmail('');
      }
    })();
  }, []);

  // 属性一覧の取得（選択肢を持つ属性のみに絞り込み）
  const loadAttributes = useCallback(async () => {
    if (!canEdit) {
      setAttributes([]);
      return;
    }
    if (!division || !team) {
      setAttributes([]);
      return;
    }
    try {
      const res = await apiClient.get(API_ENDPOINTS.GET_ATTRIBUTES, {
        params: { department_id: division, team_id: team },
      });
      const rows: AttributeRow[] = (Array.isArray(res?.data) ? res.data : [])
        .filter((a: any) => OPTION_ATTR_TYPES.has(Number(a?.attr_type)) && String(a?.status ?? 'Y') !== 'N')
        .map((a: any) => ({
          attr_id: Number(a.attr_id),
          attr_name: String(a.attr_name ?? a.label ?? ''),
          attr_type: Number(a.attr_type),
          department_id: Number(a.department_id),
          team_id: Number(a.team_id),
          status: String(a.status ?? 'Y'),
          options: (Array.isArray(a.options) ? a.options : [])
            .filter((o: any) => String(o?.status ?? 'Y') !== 'N')
            .map((o: any) => ({
              option_id: Number(o.option_id),
              value: String(o.value ?? ''),
              label: String(o.label ?? o.value ?? ''),
              color: o.color ?? null,
              order_number: Number(o.order_number ?? 0),
              status: String(o.status ?? 'Y'),
            })),
        }));

      // UI_VISIBILITY.CLASSIFICATION_MARKET_ATTRIBUTE_SELECTOR が false の間は
      // 「週報分類」属性のみに絞り込む（選択セレクタ自体の機能は保持し、非表示のみ）
      const visibleRows = UI_VISIBILITY.CLASSIFICATION_MARKET_ATTRIBUTE_SELECTOR
        ? rows
        : rows.filter((r) => r.attr_name.trim() === CLASSIFICATION_MARKET_TARGET_ATTR_NAME);

      setAttributes(visibleRows);
      setAttrId((prev) => {
        if (prev && visibleRows.some((r) => r.attr_id === prev)) return prev;
        return visibleRows.length > 0 ? visibleRows[0].attr_id : null;
      });
    } catch (e) {
      console.error('get_attributes error', e);
      setAttributes([]);
    }
  }, [division, team, canEdit]);

  useEffect(() => { loadAttributes(); }, [loadAttributes]);

  // マーケット一覧の取得（自課から見える公開範囲のもの）
  const loadMarketItems = useCallback(async () => {
    if (!canEdit) {
      setMarketItems([]);
      return;
    }
    if (!division) {
      setMarketItems([]);
      return;
    }
    try {
      const res = await apiClient.get(API_ENDPOINTS.GET_ATTRIBUTE_OPTIONS_MARKET, {
        params: { viewer_department_id: division, include_disabled: true },
      });
      setMarketItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('get_attribute_options_market error', e);
      setMarketItems([]);
    }
  }, [division, canEdit]);

  useEffect(() => { loadMarketItems(); }, [loadMarketItems]);

  // 輸入実績の取得（現在の課・チーム・属性）
  const loadUsingItems = useCallback(async () => {
    if (!canEdit) {
      setUsingItems([]);
      return;
    }
    if (!division || !team || !attrId) {
      setUsingItems([]);
      return;
    }
    try {
      const res = await apiClient.get(API_ENDPOINTS.GET_ATTRIBUTE_OPTIONS_MARKET_USING, {
        params: { department_id: division, team_id: team, attr_id: attrId },
      });
      setUsingItems(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('get_attribute_options_market_using error', e);
      setUsingItems([]);
    }
  }, [division, team, attrId, canEdit]);

  useEffect(() => { loadUsingItems(); }, [loadUsingItems]);

  const currentAttribute = useMemo(
    () => attributes.find((a) => a.attr_id === attrId) ?? null,
    [attributes, attrId]
  );

  const publishedOptionIds = useMemo(
    () => new Set(
      marketItems
        .filter((m) => m.status === 'Y')
        .map((m) => m.source_option_id)
        .filter((v): v is number => v != null)
    ),
    [marketItems]
  );

  // 他課から輸入した選択肢（imported_option_id）は、輸入元と別物として再公開できないようにする
  const importedOptionIds = useMemo(
    () => new Set(usingItems.filter((u) => u.status === 'Y').map((u) => u.imported_option_id)),
    [usingItems]
  );

  const importedMarketIds = useMemo(
    () => new Set(usingItems.filter((u) => u.status === 'Y').map((u) => u.market_option_id)),
    [usingItems]
  );

  const divisionSelectOptions: Option[] = useMemo(
    () => (Array.isArray(divisionOptions) ? divisionOptions : []).map((d: any) => ({
      value: String(d.department_id),
      label: String(d.label ?? d.value ?? ''),
    })),
    [divisionOptions]
  );
  const teamSelectOptions: Option[] = useMemo(
    () => (Array.isArray(teamOptions) ? teamOptions : [])
      .filter((t: any) => Number(t.department_id) === Number(division))
      .map((t: any) => ({ value: String(t.team_id), label: String(t.label ?? t.value ?? '') })),
    [teamOptions, division]
  );
  const attrSelectOptions: Option[] = useMemo(
    () => attributes.map((a) => ({ value: String(a.attr_id), label: a.attr_name })),
    [attributes]
  );
  const marketCategorySelectOptions: Option[] = useMemo(
    () => [
      { value: '', label: 'すべて' },
      ...MARKET_CATEGORY_OPTIONS.map((c) => ({ value: String(c.value), label: c.label })),
    ],
    []
  );

  const otherDivisionOptions = useMemo(
    () => (Array.isArray(divisionOptions) ? divisionOptions : []).filter(
      (d: any) => Number(d.department_id) !== Number(division)
    ),
    [divisionOptions, division]
  );

  const departmentLabel = useCallback(
    (departmentId: number) =>
      (Array.isArray(divisionOptions) ? divisionOptions : [])
        .find((d: any) => Number(d.department_id) === departmentId)?.label ?? `課ID:${departmentId}`,
    [divisionOptions]
  );

  // 輸入で作成された選択肢(option_id) -> 輸入元の課名
  const importedOptionSourceLabel = useMemo(() => {
    const map = new Map<number, string>();
    usingItems
      .filter((u) => u.status === 'Y')
      .forEach((u) => {
        const market = marketItems.find((m) => m.market_option_id === u.market_option_id);
        if (market) {
          map.set(u.imported_option_id, departmentLabel(market.source_department_id));
        }
      });
    return map;
  }, [usingItems, marketItems, departmentLabel]);

  // 現在有効な輸入実績を、マーケット側の情報と結合する。
  // 輸入から7日以内は「直近の輸入履歴」として取り消し可能に、それ以前は通常の履歴として表示する。
  const RECENT_IMPORT_DAYS = 7;
  const combinedImports = useMemo(
    () =>
      usingItems
        .filter((u) => u.status === 'Y')
        .map((u) => {
          const market = marketItems.find((m) => m.market_option_id === u.market_option_id);
          return market ? { using: u, market } : null;
        })
        .filter((v): v is { using: MarketUsingItem; market: MarketItem } => v != null),
    [usingItems, marketItems]
  );

  const recentImports = useMemo(() => {
    const threshold = Date.now() - RECENT_IMPORT_DAYS * 24 * 60 * 60 * 1000;
    return combinedImports.filter(({ using }) => {
      const t = using.create_dt ? new Date(using.create_dt).getTime() : NaN;
      return Number.isFinite(t) && t >= threshold;
    });
  }, [combinedImports]);

  const olderImports = useMemo(() => {
    const threshold = Date.now() - RECENT_IMPORT_DAYS * 24 * 60 * 60 * 1000;
    return combinedImports.filter(({ using }) => {
      const t = using.create_dt ? new Date(using.create_dt).getTime() : NaN;
      return !(Number.isFinite(t) && t >= threshold);
    });
  }, [combinedImports]);

  const filteredMarketItems = useMemo(() => {
    const q = marketSearch.trim().toLowerCase();
    return marketItems.filter((item) => {
      if (marketCategoryFilter && String(item.category_id) !== marketCategoryFilter) return false;
      if (!q) return true;
      return (
        item.label.toLowerCase().includes(q) ||
        departmentLabel(item.source_department_id).toLowerCase().includes(q)
      );
    });
  }, [marketItems, marketSearch, marketCategoryFilter, departmentLabel]);

  const handleOpenPublishDialog = (option: AttributeOptionRow) => {
    setPublishTarget(option);
    setPublishScope('public');
    setPublishTargetDepts([]);
    setPublishCategory(MARKET_CATEGORY_OPTIONS[0].value);
  };

  const handleClosePublishDialog = () => setPublishTarget(null);

  const handleSubmitPublish = async () => {
    if (!publishTarget || !currentAttribute) return;
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.ADD_ATTRIBUTE_OPTIONS_MARKET, {
        value: publishTarget.value,
        label: publishTarget.label,
        color: publishTarget.color,
        source_department_id: division,
        source_team_id: team,
        source_attr_id: currentAttribute.attr_id,
        source_option_id: publishTarget.option_id,
        category_id: publishCategory,
        visibility_scope: publishScope,
        target_department_ids: publishScope === 'limited' ? publishTargetDepts : undefined,
        create_by: email,
      });
      await loadMarketItems();
      setPublishTarget(null);
    } catch (e) {
      console.error('add_attribute_options_market error', e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMarketStatus = async (item: MarketItem) => {
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.UPDATE_ATTRIBUTE_OPTIONS_MARKET, {
        market_option_id: item.market_option_id,
        status: item.status === 'Y' ? 'N' : 'Y',
        update_by: email,
      });
      await loadMarketItems();
    } catch (e) {
      console.error('update_attribute_options_market error', e);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (item: MarketItem) => {
    if (!division || !team || !attrId) return;
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.IMPORT_ATTRIBUTE_OPTIONS_MARKET, {
        market_option_id: item.market_option_id,
        department_id: division,
        team_id: team,
        attr_id: attrId,
        imported_by: email,
      });
      await Promise.all([loadUsingItems(), loadAttributes()]);
    } catch (e) {
      console.error('import_attribute_options_market error', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelImport = async (using: MarketUsingItem) => {
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.CANCEL_ATTRIBUTE_OPTIONS_MARKET_IMPORT, {
        market_option_id: using.market_option_id,
        department_id: using.department_id,
        team_id: using.team_id,
        attr_id: using.attr_id,
        cancel_by: email,
      });
      await Promise.all([loadUsingItems(), loadAttributes()]);
    } catch (e) {
      console.error('cancel_attribute_options_market_import error', e);
    } finally {
      setLoading(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="classification-market-page">
        <Tabs />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Typography variant="h6">本機能は管理者のみ操作できます</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="classification-market-page">
      <Tabs />
      <div className="classification-market-header-row">
        <Chip
          icon={<ManageAccountsIcon sx={{ color: '#c9a300 !important' }} />}
          label="管理者限定機能"
          variant="outlined"
          sx={{
            flexShrink: 0,
            borderColor: '#c9a300',
            borderWidth: 2,
            borderRadius: '16px',
            color: '#c9a300',
            fontWeight: 700,
            '& .MuiChip-label': { px: 1.5 },
          }}
        />
        <Typography variant="body2" className="classification-market-description">
          分類市場は"週報分類"を課を超えて共有できる機能です。
          <br />
          あなたの課で作成した分類を他の課に公開したり、公開されている分類を輸入することができます。
        </Typography>
      </div>
      <div className="classification-market-container">
        <div className="classification-market-columns">
          <Card variant="outlined" className="classification-market-card">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>対象の選択</Typography>
              <div className="classification-market-filters">
                <div className="classification-market-filter-item">
                  <label>課</label>
                  <CommonSelect
                    id="cm-division"
                    options={divisionSelectOptions}
                    value={division ? divisionSelectOptions.find((o) => o.value === String(division)) ?? null : null}
                    onChange={(opt) => setDivision(opt ? Number(opt.value) : 0)}
                    isClearable={false}
                  />
                </div>
                <div className="classification-market-filter-item">
                  <label>チーム</label>
                  <CommonSelect
                    id="cm-team"
                    options={teamSelectOptions}
                    value={team ? teamSelectOptions.find((o) => o.value === String(team)) ?? null : null}
                    onChange={(opt) => setTeam(opt ? Number(opt.value) : 0)}
                    isClearable={false}
                  />
                </div>
                {UI_VISIBILITY.CLASSIFICATION_MARKET_ATTRIBUTE_SELECTOR && (
                  <div className="classification-market-filter-item">
                    <label>週報分類（選択式の属性）</label>
                    <CommonSelect
                      id="cm-attr"
                      options={attrSelectOptions}
                      value={attrId ? attrSelectOptions.find((o) => o.value === String(attrId)) ?? null : null}
                      onChange={(opt) => setAttrId(opt ? Number(opt.value) : null)}
                      isClearable={false}
                      noOptionsMessage="選択式の属性がありません"
                    />
                  </div>
                )}
              </div>
              {!UI_VISIBILITY.CLASSIFICATION_MARKET_ATTRIBUTE_SELECTOR && division > 0 && team > 0 && !currentAttribute && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  このチームには「{CLASSIFICATION_MARKET_TARGET_ATTR_NAME}」属性が見つかりません。属性名をご確認ください。
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {recentImports.length > 0 && (
                <div className="classification-market-recent-imports">
                  <Typography variant="subtitle2" gutterBottom>直近の輸入履歴（7日以内・取り消し可）</Typography>
                  <Table size="small">
                    <TableBody>
                      {recentImports.map(({ using, market }) => (
                        <TableRow key={using.market_option_id}>
                          <TableCell>{market.label}</TableCell>
                          <TableCell>
                            {market.color ? (
                              <span className="classification-market-color-swatch" style={{ backgroundColor: market.color }} />
                            ) : '-'}
                          </TableCell>
                          <TableCell>{departmentLabel(market.source_department_id)}</TableCell>
                          <TableCell>
                            {market.visibility_scope === 'public' ? (
                              <Chip size="small" label="全公開" />
                            ) : (
                              <Chip size="small" label="一部公開" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!canEdit}
                              onClick={() => handleCancelImport(using)}
                            >
                              輸入取り消し
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {olderImports.length > 0 && (
                <div className="classification-market-older-imports">
                  <Typography variant="subtitle2" gutterBottom>輸入履歴（7日以上前）</Typography>
                  <Table size="small">
                    <TableBody>
                      {olderImports.map(({ using, market }) => (
                        <TableRow key={using.market_option_id}>
                          <TableCell>{market.label}</TableCell>
                          <TableCell>
                            {market.color ? (
                              <span className="classification-market-color-swatch" style={{ backgroundColor: market.color }} />
                            ) : '-'}
                          </TableCell>
                          <TableCell>{departmentLabel(market.source_department_id)}</TableCell>
                          <TableCell>
                            {market.visibility_scope === 'public' ? (
                              <Chip size="small" label="全公開" />
                            ) : (
                              <Chip size="small" label="一部公開" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                新しい週報分類を共有
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                選択中の属性「{currentAttribute?.attr_name ?? '-'}」の選択肢を、分類市場に公開できます。
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>値</TableCell>
                    <TableCell>色</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(currentAttribute?.options ?? []).map((opt) => {
                    const published = publishedOptionIds.has(opt.option_id);
                    const imported = importedOptionIds.has(opt.option_id);
                    const publishTooltip = imported
                      ? '他課から輸入した分類は公開できません（自分の課で作成した分類のみ公開可能です）'
                      : published
                      ? '既に分類市場に公開済みです'
                      : 'この選択肢を分類市場に公開する';
                    return (
                      <TableRow key={opt.option_id}>
                        <TableCell>{opt.label}</TableCell>
                        <TableCell>
                          {opt.color ? (
                            <span className="classification-market-color-swatch" style={{ backgroundColor: opt.color }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {published ? (
                            <Chip size="small" color="info" icon={<CheckCircleIcon />} label="公開中" />
                          ) : imported ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${importedOptionSourceLabel.get(opt.option_id) ?? '不明'}から輸入`}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" label="未公開" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={publishTooltip}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PublicIcon />}
                                disabled={!canEdit || published || imported}
                                onClick={() => handleOpenPublishDialog(opt)}
                              >
                                公開
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!currentAttribute || (currentAttribute.options ?? []).length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">選択肢がありません</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="classification-market-flow-arrows">
            <div className="classification-market-flow-arrow classification-market-flow-arrow--import">
              <ArrowBackIcon fontSize="small" />
              <span>輸入</span>
            </div>
            <div className="classification-market-flow-arrow classification-market-flow-arrow--publish">
              <span>公開</span>
              <ArrowForwardIcon fontSize="small" />
            </div>
          </div>

          <Card variant="outlined" className="classification-market-card classification-market-market-card">
            <div className="classification-market-market-header">
              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700 }}>
                Weekly Report Category Market
              </Typography>
            </div>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                共有マーケットから輸入
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom style={{ marginBottom: 16 }}>
                他課が公開したテンプレートを、選択中の属性「{currentAttribute?.attr_name ?? '-'}」の選択肢として取り込めます。
              </Typography>
              <div className="classification-market-filters" style={{ marginBottom: 16 }}>
                <div className="classification-market-filter-item">
                  <label>部門</label>
                  <CommonSelect
                    id="cm-market-category"
                    options={marketCategorySelectOptions}
                    value={marketCategorySelectOptions.find((o) => o.value === marketCategoryFilter) ?? null}
                    onChange={(opt) => setMarketCategoryFilter(opt ? opt.value : '')}
                    isClearable={false}
                  />
                </div>
                <div className="classification-market-filter-item" style={{ flex: 1 }}>
                  <label>検索</label>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="値または発信元の課名で検索"
                    value={marketSearch}
                    onChange={(e) => setMarketSearch(e.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              </div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>値</TableCell>
                    <TableCell>色</TableCell>
                    <TableCell>発信元</TableCell>
                    <TableCell>分類</TableCell>
                    <TableCell>公開範囲</TableCell>
                    <TableCell align="right">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMarketItems.map((item) => {
                    const imported = importedMarketIds.has(item.market_option_id);
                    const isOwn = item.source_department_id === division;
                    return (
                      <TableRow key={item.market_option_id}>
                        <TableCell>{item.label}</TableCell>
                        <TableCell>
                          {item.color ? (
                            <span className="classification-market-color-swatch" style={{ backgroundColor: item.color }} />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{departmentLabel(item.source_department_id)}</TableCell>
                        <TableCell>
                          {MARKET_CATEGORY_OPTIONS.find((c) => c.value === item.category_id)?.label ?? '-'}
                        </TableCell>
                        <TableCell>
                          {item.visibility_scope === 'public' ? (
                            <Chip size="small" label="全公開" />
                          ) : (
                            <Chip size="small" label="一部公開" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isOwn ? (
                            <Tooltip title={item.status === 'Y' ? 'マーケットから無効化する' : 'マーケットへ再度有効化する'}>
                              <span>
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={!canEdit}
                                  onClick={() => handleToggleMarketStatus(item)}
                                >
                                  {item.status === 'Y' ? '無効化' : '有効化'}
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title={imported ? '既に輸入済みです' : 'この選択肢を自分の属性に追加する'}>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<DownloadIcon />}
                                  disabled={!canEdit || imported || !attrId || item.status !== 'Y'}
                                  onClick={() => handleImport(item)}
                                >
                                  {imported ? '輸入済み' : '輸入'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredMarketItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          {marketItems.length === 0 ? '公開されているテンプレートがありません' : '該当するテンプレートがありません'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={Boolean(publishTarget)} onClose={handleClosePublishDialog} fullWidth maxWidth="xs">
        <DialogTitle>分類市場へ公開</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            「{publishTarget?.label}」を分類市場へ公開します。公開範囲を選択してください。
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            label="分類"
            value={publishCategory}
            onChange={(e) => setPublishCategory(Number(e.target.value))}
            sx={{ mb: 2 }}
          >
            {MARKET_CATEGORY_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <RadioGroup
            value={publishScope}
            onChange={(e) => setPublishScope(e.target.value as 'public' | 'limited')}
          >
            <FormControlLabel value="public" control={<Radio />} label="全課に公開" />
            <FormControlLabel value="limited" control={<Radio />} label="指定した課のみに公開" />
          </RadioGroup>
          {publishScope === 'limited' && (
            <div className="classification-market-target-departments">
              {otherDivisionOptions.map((d: any) => {
                const deptId = Number(d.department_id);
                const checked = publishTargetDepts.includes(deptId);
                return (
                  <FormControlLabel
                    key={deptId}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          setPublishTargetDepts((prev) =>
                            e.target.checked ? [...prev, deptId] : prev.filter((id) => id !== deptId)
                          );
                        }}
                      />
                    }
                    label={d.label}
                  />
                );
              })}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePublishDialog}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSubmitPublish}
            disabled={publishScope === 'limited' && publishTargetDepts.length === 0}
          >
            公開する
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default ClassificationMarketPage;
